/**
 * tn-dsp.js — математика /tinnitus-notch: нотч, окраска, шум, анализ источника.
 * window.TinnitusDSP (и module.exports для узловых тестов).
 *
 * ЗАЧЕМ ОТДЕЛЬНЫЙ МОДУЛЬ. Предпрослушка и скачанный файл обязаны звучать
 * одинаково, а график — показывать ровно то, что звучит. Поэтому коэффициенты
 * фильтра считаются ЗДЕСЬ, один раз, и из одного и того же набора секций
 * строятся и живой граф (IIRFilterNode на секцию), и экспорт (sosProcess в
 * float64), и кривая на экране (sosResponse). Три потребителя — одна истина.
 *
 * ПОЧЕМУ ЧЕБЫШЁВ II, А НЕ ДВА «КОЛОКОЛА». До 29.09.2026 нотч строился двумя
 * peaking-фильтрами по −18 дБ в центре частоты. Замер той конструкции (½ окт,
 * 4 кГц): в центре −36 дБ, но уже на 80 % ширины полосы — только −20 дБ, на
 * номинальных краях −17 дБ, а за краем на 1/8 октавы ещё −12 дБ и на 1/4 —
 * −8.7 дБ. То есть полоса вырезалась не целиком, а соседние частоты — те, на
 * работе которых метод и держится (латеральное торможение), — глушились.
 * В исследованиях полоса удалялась целиком (Teismann 2011: Баттерворт
 * 150-го порядка). Полосовой режектор Чебышёва II даёт ровное дно ≥ 50 дБ по
 * ВСЕЙ полосе и крутые края: порядок подобран так (замер в scipy, 44.1 и
 * 48 кГц, центр 250 Гц…11 кГц), что за краем на 1/16 октавы потеря ≤ 1 дБ:
 *   ½ окт → N = 10, 1 окт → N = 14, 1½ окт → N = 16.
 * Формула порядка: N = 2·⌈4·W + 3⌉, в пределах 8…18.
 * Почему 50, а не 40 или 60: 40 дБ MP3 320 держит без потерь (замер 29.09:
 * ±0.6 дБ на 2–12 кГц), 60 дБ стоили бы порядка 20 на 1½ октавы; 50 — запас
 * над кодеком при умеренном порядке.
 *
 * ПОЧЕМУ СЕКЦИИ НОРМИРУЮТСЯ НА ЕДИНИЦУ В НУЛЕ. У режектора усиление на
 * постоянном токе и на Найквисте равно 1, поэтому, приводя каждую секцию к
 * единичному усилению на нулевой частоте, мы получаем верное общее усиление
 * без отдельного множителя и не раздуваем сигнал между узлами графа.
 *
 * Алгоритм расчёта — тот же, что у scipy.signal.iirfilter(ftype='cheby2',
 * btype='bandstop'): прототип cheb2ap → lp2bs_zpk → bilinear_zpk (с
 * предыскажением частот) → секции второго порядка. Сверка с числами scipy —
 * в tests/js/tinnitus-notch-dsp.test.mjs.
 */
(function (root) {
'use strict';

/* ── комплексная арифметика: {re, im} ─────────────────────────────────────── */
function cx(re, im) { return { re: re, im: im || 0 }; }
function cadd(a, b) { return cx(a.re + b.re, a.im + b.im); }
function csub(a, b) { return cx(a.re - b.re, a.im - b.im); }
function cmul(a, b) { return cx(a.re * b.re - a.im * b.im, a.re * b.im + a.im * b.re); }
function cdiv(a, b) {
    var d = b.re * b.re + b.im * b.im;
    return cx((a.re * b.re + a.im * b.im) / d, (a.im * b.re - a.re * b.im) / d);
}
function cscale(a, s) { return cx(a.re * s, a.im * s); }
function cabs(a) { return Math.hypot(a.re, a.im); }
/* Главная ветвь корня (Re ≥ 0), как у numpy для комплексного аргумента. */
function csqrt(a) {
    var m = cabs(a);
    var re = Math.sqrt((m + a.re) / 2);
    var im = Math.sqrt(Math.max(0, (m - a.re) / 2));
    if (a.im < 0) im = -im;
    return cx(re, im);
}

/* ── аналоговый прототип Чебышёва II, край полосы задерживания на ω = 1 ──── */
function cheb2ap(N, rs) {
    var de = 1 / Math.sqrt(Math.pow(10, 0.1 * rs) - 1);
    var mu = Math.asinh(1 / de) / N;
    var m = [], i;
    if (N % 2) {
        for (i = -N + 1; i < 0; i += 2) m.push(i);
        for (i = 2; i < N; i += 2) m.push(i);
    } else {
        for (i = -N + 1; i < N; i += 2) m.push(i);
    }
    /* z = −conj(1j / sin(mπ/2N)) = j / sin(mπ/2N) */
    var z = m.map(function (mm) { return cx(0, 1 / Math.sin(mm * Math.PI / (2 * N))); });
    var p = [];
    for (i = -N + 1; i < N; i += 2) {
        var ang = Math.PI * i / (2 * N);
        var q = cx(-Math.cos(ang), -Math.sin(ang));          // −exp(jπi/2N)
        q = cx(Math.sinh(mu) * q.re, Math.cosh(mu) * q.im);   // «растяжение» в Чебышёва II
        p.push(cdiv(cx(1, 0), q));                            // 1/p
    }
    var np = cx(1, 0), nz = cx(1, 0);
    p.forEach(function (x) { np = cmul(np, cscale(x, -1)); });
    z.forEach(function (x) { nz = cmul(nz, cscale(x, -1)); });
    return { z: z, p: p, k: cdiv(np, nz).re };
}

/* ── НЧ-прототип → режектор с центром wo и шириной bw ───────────────────── */
function lp2bs(z, p, k, wo, bw) {
    var degree = p.length - z.length;
    var wo2 = cx(wo * wo, 0);
    function split(list) {
        var plus = [], minus = [];
        list.forEach(function (x) {
            var h = cdiv(cx(bw / 2, 0), x);
            var r = csqrt(csub(cmul(h, h), wo2));
            plus.push(cadd(h, r));
            minus.push(csub(h, r));
        });
        return plus.concat(minus);
    }
    var zb = split(z), pb = split(p), i;
    for (i = 0; i < degree; i++) zb.push(cx(0, wo));
    for (i = 0; i < degree; i++) zb.push(cx(0, -wo));
    var nz = cx(1, 0), np = cx(1, 0);
    z.forEach(function (x) { nz = cmul(nz, cscale(x, -1)); });
    p.forEach(function (x) { np = cmul(np, cscale(x, -1)); });
    return { z: zb, p: pb, k: k * cdiv(nz, np).re };
}

/* ── билинейное преобразование (fs уже в нормированной шкале) ─────────────── */
function bilinear(z, p, k, fs) {
    var degree = p.length - z.length;
    var fs2 = cx(2 * fs, 0);
    var zz = z.map(function (x) { return cdiv(cadd(fs2, x), csub(fs2, x)); });
    var pz = p.map(function (x) { return cdiv(cadd(fs2, x), csub(fs2, x)); });
    for (var i = 0; i < degree; i++) zz.push(cx(-1, 0));
    var nz = cx(1, 0), np = cx(1, 0);
    z.forEach(function (x) { nz = cmul(nz, csub(fs2, x)); });
    p.forEach(function (x) { np = cmul(np, csub(fs2, x)); });
    return { z: zz, p: pz, k: k * cdiv(nz, np).re };
}

/* ── нули и полюсы → секции второго порядка ───────────────────────────────
   У режектора все нули и полюсы комплексные парами; берём по одному из пары
   (Im > 0) и сводим каждый полюс с ближайшим свободным нулём — так, как это
   делает scipy.zpk2sos(pairing='nearest'). Порядок: полюсы, ближайшие к
   единичной окружности, идут последними. Каждая секция приведена к
   единичному усилению на нулевой частоте. */
function zpk2sos(z, p) {
    var EPS = 1e-9;
    function upper(list) { return list.filter(function (x) { return x.im > EPS; }); }
    function reals(list) { return list.filter(function (x) { return Math.abs(x.im) <= EPS; }); }
    var zu = upper(z), pu = upper(p), zr = reals(z), pr = reals(p);
    var sections = [];
    pu.sort(function (a, b) { return (1 - cabs(b)) - (1 - cabs(a)); });   // дальние от окружности первыми
    var freeZ = zu.slice();
    pu.forEach(function (pp) {
        var best = -1, bd = Infinity;
        for (var i = 0; i < freeZ.length; i++) {
            var d = cabs(csub(freeZ[i], pp));
            if (d < bd) { bd = d; best = i; }
        }
        var zz = best >= 0 ? freeZ.splice(best, 1)[0] : null;
        var b = zz ? [1, -2 * zz.re, zz.re * zz.re + zz.im * zz.im] : [1, 0, 0];
        var a = [1, -2 * pp.re, pp.re * pp.re + pp.im * pp.im];
        sections.push(normDC(b, a));
    });
    /* Остаток (нечётный порядок, действительные корни) — секциями из пар.
       У чётного режектора его нет, но расчёт обязан не падать на любом входе. */
    while (pr.length || zr.length || freeZ.length) {
        var bz = [1, 0, 0], ap = [1, 0, 0];
        if (freeZ.length) { var q = freeZ.shift(); bz = [1, -2 * q.re, q.re * q.re + q.im * q.im]; }
        else if (zr.length) {
            var z1 = zr.shift(), z2 = zr.length ? zr.shift() : null;
            bz = z2 ? [1, -(z1.re + z2.re), z1.re * z2.re] : [1, -z1.re, 0];
        }
        if (pr.length) {
            var p1 = pr.shift(), p2 = pr.length ? pr.shift() : null;
            ap = p2 ? [1, -(p1.re + p2.re), p1.re * p2.re] : [1, -p1.re, 0];
        }
        sections.push(normDC(bz, ap));
    }
    return sections;
}
function normDC(b, a) {
    var gb = b[0] + b[1] + b[2], ga = a[0] + a[1] + a[2];
    var g = Math.abs(gb) > 1e-12 ? ga / gb : 1;
    return [b[0] * g, b[1] * g, b[2] * g, 1, a[1], a[2]];
}

/* ── ширина и края полосы ───────────────────────────────────────────────
   Ширина — число октав W (½, 1, 1½ или «по разбросу подбора»). Края —
   геометрически симметрично вокруг центра: f0·2^(±W/2), как в Teismann 2011
   (f/√2…f·√2 для октавы). Верх упирается в 0.45·fs: у файла 44.1 кГц выше
   19.8 кГц ничего нет, и режектор туда не заходит. */
function bandEdges(f0, W, fs) {
    var fl = f0 * Math.pow(2, -W / 2), fh = f0 * Math.pow(2, W / 2);
    fh = Math.min(fh, 0.45 * fs);
    fl = Math.min(fl, fh * 0.9);
    return { fl: fl, fh: fh };
}

var DEPTH_DB = 50;
var SHRINK_OCT = 1 / 32;   // полоса задерживания чуть уже номинальной: −6 дБ ложится у номинального края

function notchOrder(W) {
    /* ⚠️ W приходит пересчитанной из краёв (log(fh/fl)/LN2), и «ровно 1 октава»
       бывает 1.0000000000000002 — зависит от libm движка: Node 22 на Linux
       (CI) даёт её, Node 24 на Windows — ровно 1. Голый ceil(7.000…001) = 8
       делал порядок 16 вместо 14, то есть ОДНА настройка давала разные
       фильтры в разных браузерах (CI 30.09.2026, tinnitus-notch-dsp). Допуск
       1e-9 октавы — на шум округления, не на ширину. */
    var n = 2 * Math.ceil(4 * W + 3 - 1e-9);
    return Math.max(8, Math.min(18, n));
}

/* Полосовой режектор: секции [b0,b1,b2,1,a1,a2] для частоты fs. */
function designBandstop(fl, fh, fs, opts) {
    opts = opts || {};
    var W = Math.log(fh / fl) / Math.LN2;
    var N = opts.order || notchOrder(W);
    var rs = opts.depth || DEPTH_DB;
    var shrink = opts.shrink != null ? opts.shrink : SHRINK_OCT;
    /* Сужаем полосу задерживания внутрь, но не до нуля у очень узкой полосы. */
    var sh = Math.min(shrink, W / 8);
    var sl = fl * Math.pow(2, sh), su = fh * Math.pow(2, -sh);
    var nyq = fs / 2;
    var w1 = Math.min(0.999, sl / nyq), w2 = Math.min(0.999, su / nyq);
    /* Предыскажение, fs = 2 в нормированной шкале — как в scipy.iirfilter. */
    var FSN = 2;
    var wl = 2 * FSN * Math.tan(Math.PI * w1 / FSN);
    var wh = 2 * FSN * Math.tan(Math.PI * w2 / FSN);
    var proto = cheb2ap(N, rs);
    var bs = lp2bs(proto.z, proto.p, proto.k, Math.sqrt(wl * wh), wh - wl);
    var dz = bilinear(bs.z, bs.p, bs.k, FSN);
    return { sos: zpk2sos(dz.z, dz.p), order: N, fl: fl, fh: fh };
}

/* Пиковый фильтр — ровно формулы BiquadFilterNode 'peaking' из спецификации
   Web Audio (Q линейный). */
function peaking(fc, Q, gainDb, fs) {
    var A = Math.pow(10, gainDb / 40), w0 = 2 * Math.PI * fc / fs;
    var al = Math.sin(w0) / (2 * Q), c = Math.cos(w0);
    var a0 = 1 + al / A;
    return [(1 + al * A) / a0, (-2 * c) / a0, (1 - al * A) / a0, 1, (-2 * c) / a0, (1 - al / A) / a0];
}

/* Q для пика шириной bwOct октав (определение RBJ через ширину в октавах). */
function qFromOct(bwOct) {
    var t = Math.pow(2, bwOct);
    return Math.sqrt(t) / (t - 1);
}

/* Подъём краёв: по пику шириной ⅜ окт сразу за каждым краем полосы.
   ⅜ октавы и до +20 дБ — параметры Stein et al. (2013, 2016). Метод
   экспериментальный: в МЭГ ответ был сильнее, в оценках громкости — нет. */
var EDGE_BW_OCT = 3 / 8;
var EDGE_MAX_DB = 20;
function edgeSections(fl, fh, gainDb, fs) {
    if (!(gainDb > 0)) return [];
    gainDb = Math.min(EDGE_MAX_DB, gainDb);
    var q = qFromOct(EDGE_BW_OCT);
    var cl = fl * Math.pow(2, -EDGE_BW_OCT / 2), ch = fh * Math.pow(2, EDGE_BW_OCT / 2);
    var out = [];
    if (cl > 30) out.push(peaking(cl, q, gainDb, fs));
    if (ch < 0.45 * fs) out.push(peaking(ch, q, gainDb, fs));
    return out;
}

/* Полки — формулы 'lowshelf'/'highshelf' спецификации Web Audio (S = 1). */
function shelf(type, fc, gainDb, fs) {
    var A = Math.pow(10, gainDb / 40), w0 = 2 * Math.PI * fc / fs, c = Math.cos(w0);
    var al = Math.sin(w0) / Math.SQRT2, sa = 2 * Math.sqrt(A) * al, b0, b1, b2, a0, a1, a2;
    if (type === 'low') {
        b0 = A * ((A + 1) - (A - 1) * c + sa); b1 = 2 * A * ((A - 1) - (A + 1) * c); b2 = A * ((A + 1) - (A - 1) * c - sa);
        a0 = (A + 1) + (A - 1) * c + sa; a1 = -2 * ((A - 1) + (A + 1) * c); a2 = (A + 1) + (A - 1) * c - sa;
    } else {
        b0 = A * ((A + 1) + (A - 1) * c + sa); b1 = -2 * A * ((A - 1) + (A + 1) * c); b2 = A * ((A + 1) + (A - 1) * c - sa);
        a0 = (A + 1) - (A - 1) * c + sa; a1 = 2 * ((A - 1) - (A + 1) * c); a2 = (A + 1) - (A - 1) * c - sa;
    }
    return [b0 / a0, b1 / a0, b2 / a0, 1, a1 / a0, a2 / a0];
}

/* «Больше верхов». В исследованиях музыку перед нотчем «выравнивали» —
   переносили энергию с низов наверх (Teismann 2011, Wunderlich 2015, Stein
   2016), чтобы на частоте нотча было что вырезать; точные настройки не
   опубликованы. Здесь — мягкий наклон: −4 дБ ниже 250 Гц, +6 дБ выше 2.5 кГц,
   громкость потом возвращается компенсацией (makeupDb). */
function tiltSections(fs) {
    return [shelf('low', 250, -4, fs), shelf('high', 2500, 6, fs)];
}

/**
 * Полный набор секций для профиля.
 * opts: { notches:[{f0, W}] (1 или 2), edgeDb: 0…20, tilt: bool }.
 * Возвращает { sos, notchSos, colorSos, bands:[{f0,fl,fh,W}], order }.
 * colorSos — только окраска (наклон + края), по ней считается компенсация
 * громкости: сам нотч энергию убирает честно, её не «докручиваем».
 */
function designChain(opts, fs) {
    opts = opts || {};
    var notchSos = [], colorSos = [], bands = [], order = 0;
    if (opts.tilt) colorSos = colorSos.concat(tiltSections(fs));
    (opts.notches || []).forEach(function (n) {
        if (!n || !(n.f0 > 0)) return;
        var W = n.W || 1;
        var e = bandEdges(n.f0, W, fs);
        var d = designBandstop(e.fl, e.fh, fs);
        notchSos = notchSos.concat(d.sos);
        order += d.order;
        bands.push({ f0: n.f0, fl: e.fl, fh: e.fh, W: W });
        colorSos = colorSos.concat(edgeSections(e.fl, e.fh, opts.edgeDb || 0, fs));
    });
    return { sos: notchSos.concat(colorSos), notchSos: notchSos, colorSos: colorSos, bands: bands, order: order };
}

/* ── АЧХ набора секций, дБ ─────────────────────────────────────────────── */
function sosResponse(sos, freqs, fs) {
    var out = new Float64Array(freqs.length);
    for (var i = 0; i < freqs.length; i++) {
        var w = 2 * Math.PI * freqs[i] / fs;
        var c1 = Math.cos(w), s1 = Math.sin(w), c2 = Math.cos(2 * w), s2 = Math.sin(2 * w);
        var mag2 = 1;
        for (var k = 0; k < sos.length; k++) {
            var s = sos[k];
            var nr = s[0] + s[1] * c1 + s[2] * c2, ni = -(s[1] * s1 + s[2] * s2);
            var dr = 1 + s[4] * c1 + s[5] * c2, di = -(s[4] * s1 + s[5] * s2);
            mag2 *= (nr * nr + ni * ni) / (dr * dr + di * di);
        }
        out[i] = 10 * Math.log10(mag2 + 1e-30);
    }
    return out;
}

/* ── обработка блоками: Direct Form II transposed, float64-состояние ───────
   Одно состояние на канал; блоки, поданные подряд, дают ровно тот же
   результат, что весь поток разом (сторож в тестах). */
function makeState(sos) { return new Float64Array(sos.length * 2); }
function sosProcess(sos, state, input, output) {
    var n = input.length, k, i;
    if (output !== input) output.set(input);
    for (k = 0; k < sos.length; k++) {
        var s = sos[k], b0 = s[0], b1 = s[1], b2 = s[2], a1 = s[4], a2 = s[5];
        var z1 = state[2 * k], z2 = state[2 * k + 1];
        for (i = 0; i < n; i++) {
            var x = output[i];
            var y = b0 * x + z1;
            z1 = b1 * x - a1 * y + z2;
            z2 = b2 * x - a2 * y;
            output[i] = y;
        }
        /* Денормалы в хвосте тишины тормозят цикл в десятки раз. */
        if (Math.abs(z1) < 1e-25) z1 = 0;
        if (Math.abs(z2) < 1e-25) z2 = 0;
        state[2 * k] = z1; state[2 * k + 1] = z2;
    }
    return output;
}

/* ── шумы: детерминированный генератор с зерном ───────────────────────────
   mulberry32 — быстрый и статистически достаточный для звука. Розовый — по
   Paul Kellet (refined), коричневый — «протекающий» интегратор. Уровни
   приведены к одной RMS ≈ −20 дБFS (сторож в тестах), чтобы смена цвета не
   прыгала по громкости. У живого графа и у экспорта один и тот же генератор. */
function mulberry32(a) {
    return function () {
        a |= 0; a = a + 0x6D2B79F5 | 0;
        var t = Math.imul(a ^ a >>> 15, 1 | a);
        t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t;
        return ((t ^ t >>> 14) >>> 0) / 4294967296;
    };
}
var NOISE_RMS = 0.1;   // −20 дБFS
var NOISE_NORM = { white: 0.1732, pink: 0.515, brown: 0.497 };
function noiseGen(type, seed) {
    var rnd = mulberry32(seed >>> 0 || 1);
    var b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0, last = 0;
    var g = NOISE_NORM[type] || NOISE_NORM.pink;
    return {
        fill: function (out, n) {
            n = n || out.length;
            var i, w;
            if (type === 'white') {
                for (i = 0; i < n; i++) out[i] = (rnd() * 2 - 1) * g;
            } else if (type === 'brown') {
                for (i = 0; i < n; i++) {
                    w = rnd() * 2 - 1;
                    last = (last + 0.02 * w) / 1.02;
                    out[i] = last * 3.5 * g;
                }
            } else {
                for (i = 0; i < n; i++) {
                    w = rnd() * 2 - 1;
                    b0 = 0.99886 * b0 + w * 0.0555179; b1 = 0.99332 * b1 + w * 0.0750759;
                    b2 = 0.96900 * b2 + w * 0.1538520; b3 = 0.86650 * b3 + w * 0.3104856;
                    b4 = 0.55000 * b4 + w * 0.5329522; b5 = -0.7616 * b5 - w * 0.0168980;
                    out[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + w * 0.5362) * 0.11 * g;
                    b6 = w * 0.115926;
                }
            }
            return out;
        }
    };
}

/* ── спектр: радикс-2 БПФ для анализа источника ────────────────────────── */
function fftInPlace(re, im) {
    var n = re.length, i, j, k, len;
    for (i = 1, j = 0; i < n; i++) {
        var bit = n >> 1;
        for (; j & bit; bit >>= 1) j ^= bit;
        j ^= bit;
        if (i < j) { var tr = re[i]; re[i] = re[j]; re[j] = tr; var ti = im[i]; im[i] = im[j]; im[j] = ti; }
    }
    for (len = 2; len <= n; len <<= 1) {
        var ang = -2 * Math.PI / len, wr = Math.cos(ang), wi = Math.sin(ang), h = len >> 1;
        for (i = 0; i < n; i += len) {
            var cr = 1, ci = 0;
            for (k = 0; k < h; k++) {
                var ur = re[i + k], ui = im[i + k];
                var vr = re[i + k + h] * cr - im[i + k + h] * ci;
                var vi = re[i + k + h] * ci + im[i + k + h] * cr;
                re[i + k] = ur + vr; im[i + k] = ui + vi;
                re[i + k + h] = ur - vr; im[i + k + h] = ui - vi;
                var nr = cr * wr - ci * wi; ci = cr * wi + ci * wr; cr = nr;
            }
        }
    }
}

var FFT_N = 4096;
/**
 * Средний спектр мощности (Уэлч, окно Ханна). channels — массив Float32Array.
 * maxFrames ограничивает работу на длинном треке: кадры берутся равномерно по
 * всей длине, а не с начала (вступление песни часто тише и беднее).
 * Возвращает { fs, bin: Гц на бин, psd: Float64Array(FFT_N/2+1) }.
 */
function spectrumProfile(channels, fs, maxFrames) {
    var N = FFT_N, half = N / 2, len = channels[0].length;
    var psd = new Float64Array(half + 1);
    var win = new Float64Array(N), i, c;
    for (i = 0; i < N; i++) win[i] = 0.5 - 0.5 * Math.cos(2 * Math.PI * i / (N - 1));
    var total = Math.max(1, Math.floor((len - N) / (N / 2)) + 1);
    var frames = Math.min(total, maxFrames || 1200);
    var step = total > frames ? (len - N) / Math.max(1, frames - 1) : N / 2;
    var re = new Float64Array(N), im = new Float64Array(N), used = 0;
    for (var f = 0; f < frames; f++) {
        var start = Math.min(len - N, Math.round(f * step));
        if (start < 0) break;
        for (i = 0; i < N; i++) {
            var v = 0;
            for (c = 0; c < channels.length; c++) v += channels[c][start + i];
            re[i] = (v / channels.length) * win[i]; im[i] = 0;
        }
        fftInPlace(re, im);
        for (i = 0; i <= half; i++) psd[i] += re[i] * re[i] + im[i] * im[i];
        used++;
    }
    if (used) for (i = 0; i <= half; i++) psd[i] /= used;
    return { fs: fs, bin: fs / N, psd: psd };
}

/* Спектр из данных AnalyserNode (дБ на бин) — для живой оценки, когда трек
   слишком велик, чтобы декодировать его целиком. */
function profileFromAnalyser(dbBins, fs, fftSize) {
    var half = FFT_N / 2, psd = new Float64Array(half + 1), bin = fs / FFT_N, src = fs / fftSize;
    for (var i = 1; i <= half; i++) {
        var j = Math.min(dbBins.length - 1, Math.round(i * bin / src));
        var d = dbBins[j];
        psd[i] = isFinite(d) ? Math.pow(10, d / 10) : 0;
    }
    return { fs: fs, bin: bin, psd: psd };
}

/* A-взвешивание (IEC 61672), линейная мощность. */
function aWeightPow(f) {
    var f2 = f * f;
    var ra = (12194 * 12194 * f2 * f2) /
        ((f2 + 20.6 * 20.6) * Math.sqrt((f2 + 107.7 * 107.7) * (f2 + 737.9 * 737.9)) * (f2 + 12194 * 12194));
    return ra * ra * 1.5849;   // +2.0 дБ нормировки на 1 кГц
}

/* Доля слышимой (A-взвешенной) энергии источника в полосе [fl, fh].
   colorSos — окраска перед нотчем («больше верхов»): она меняет, сколько
   энергии доходит до полосы. */
function bandShare(profile, fl, fh, colorSos) {
    var psd = profile.psd, bin = profile.bin, all = 0, band = 0, gain = null, i;
    if (colorSos && colorSos.length) {
        var fr = [];
        for (i = 0; i < psd.length; i++) fr.push(Math.max(1, i * bin));
        gain = sosResponse(colorSos, fr, profile.fs);
    }
    for (i = 1; i < psd.length; i++) {
        var f = i * bin;
        if (f < 40 || f > 20000) continue;
        var e = psd[i] * aWeightPow(f) * (gain ? Math.pow(10, gain[i] / 10) : 1);
        all += e;
        if (f >= fl && f <= fh) band += e;
    }
    return all > 0 ? band / all : 0;
}

/* Та же доля у розового шума — эталон «энергии достаточно». Считается
   аналитически: у розового мощность на бин ∝ 1/f. */
function pinkShare(fl, fh, fs) {
    var bin = fs / FFT_N, all = 0, band = 0;
    for (var i = 1; i <= FFT_N / 2; i++) {
        var f = i * bin;
        if (f < 40 || f > 20000) continue;
        var e = aWeightPow(f) / f;
        all += e;
        if (f >= fl && f <= fh) band += e;
    }
    return all > 0 ? band / all : 0;
}

/**
 * Подходит ли источник для нотча на этой частоте.
 * score — дБ относительно розового шума (0 = как у розового).
 * grade: 'good' ≥ −10 дБ · 'weak' −10…−20 · 'poor' < −20.
 * Калибровка 29.09.2026: музыка из фикстуры читается good до 8 кГц (−8.3),
 * weak на 10 кГц (−12.7), poor на 12 кГц (−36, срез кодека), коричневый шум —
 * good до 14 кГц (−8.1), синтетические тоны — poor везде (−60…−76). Это
 * совпадает с опубликованной таблицей AudioNotch «эффективно до»: музыка
 * 8 кГц, коричневый шум 13 кГц.
 */
function suitability(profile, fl, fh, colorSos) {
    var s = bandShare(profile, fl, fh, colorSos), ref = pinkShare(fl, fh, profile.fs);
    if (!(s > 0) || !(ref > 0)) return { score: -99, grade: 'poor' };
    var score = 10 * Math.log10(s / ref);
    return { score: score, grade: score >= -10 ? 'good' : score >= -20 ? 'weak' : 'poor' };
}

/* Компенсация громкости окраски: насколько A-взвешенная энергия источника
   выросла от наклона и подъёма краёв — на столько и убавляем. profile —
   спектр источника или null (тогда розовый шум). */
function makeupDb(colorSos, profile, fs) {
    if (!colorSos || !colorSos.length) return 0;
    var sr = profile ? profile.fs : fs, bin = sr / FFT_N, i, f, freqs = [], wts = [];
    for (i = 1; i <= FFT_N / 2; i++) {
        f = i * bin;
        if (f < 40 || f > 20000) continue;
        freqs.push(f);
        wts.push(aWeightPow(f) * (profile ? profile.psd[i] : 1 / f));
    }
    var r = sosResponse(colorSos, freqs, sr), before = 0, after = 0;
    for (i = 0; i < freqs.length; i++) { before += wts[i]; after += wts[i] * Math.pow(10, r[i] / 10); }
    if (!(before > 0) || !(after > 0)) return 0;
    return -10 * Math.log10(after / before);
}

/* ── равная громкость тонов при подборе ────────────────────────────────────
   Изофона 40 фон (ISO 226:2003), дБ УЗД. Тон на 250 Гц и на 8 кГц при одном
   уровне сигнала звучат по-разному громко, и человек выбирает «громче», а не
   «похоже». Поправка ограничена ±12 дБ: слух у людей с шумом в ушах часто
   снижен именно на высоких, и слепая компенсация по норме могла бы оглушить. */
var ISO40 = [[20, 99.85], [25, 93.94], [31.5, 88.17], [40, 82.63], [50, 77.78], [63, 73.08], [80, 68.48],
    [100, 64.37], [125, 60.59], [160, 56.7], [200, 53.41], [250, 50.4], [315, 47.58], [400, 44.98],
    [500, 43.05], [630, 41.34], [800, 40.06], [1000, 40.01], [1250, 41.82], [1600, 42.51], [2000, 39.23],
    [2500, 36.51], [3150, 35.61], [4000, 36.65], [5000, 40.01], [6300, 45.83], [8000, 51.8],
    [10000, 54.28], [12500, 51.49], [16000, 60]];
function loudnessCompDb(f) {
    var L = ISO40[ISO40.length - 1][1];
    if (f <= ISO40[0][0]) L = ISO40[0][1];
    else if (f < ISO40[ISO40.length - 1][0]) {
        var lf = Math.log(f);
        for (var i = 1; i < ISO40.length; i++) {
            if (f <= ISO40[i][0]) {
                var f0 = Math.log(ISO40[i - 1][0]), f1 = Math.log(ISO40[i][0]);
                var t = (lf - f0) / (f1 - f0);
                L = ISO40[i - 1][1] + t * (ISO40[i][1] - ISO40[i - 1][1]);
                break;
            }
        }
    }
    return Math.max(-12, Math.min(12, L - 40));
}

/* ── подбор частоты: сводка серии ────────────────────────────────────────
   Высоту звона люди подбирают нестабильно: повторы расходятся на ½–1 октаву
   (Neff 2019, Hoare 2014), а ошибка на октаву — частая. Поэтому серия из трёх
   подборов, первый пробный; центр — среднее в центах (так усредняла Stein
   2016), разброс — max−min в октавах. Ширина нотча «Авто»:
     разброс ≤ ½ окт → ½ окт (если принятых подборов ≥ 2), иначе 1 окт;
     ½…1 окт → разброс + ¼, не больше 1½;
     > 1 окт → 1 окт и совет повторить в другой день. */
function matchSummary(matches) {
    var list = (matches || []).filter(function (m) { return m && m.f > 0 && !m.practice; });
    if (!list.length) list = (matches || []).filter(function (m) { return m && m.f > 0; });
    if (!list.length) return null;
    var logs = list.map(function (m) { return Math.log(m.f) / Math.LN2; });
    var mean = logs.reduce(function (a, b) { return a + b; }, 0) / logs.length;
    var spread = list.length > 1 ? Math.max.apply(null, logs) - Math.min.apply(null, logs) : null;
    var W, state;
    if (spread == null) { W = 1; state = 'single'; }
    else if (spread <= 0.5) { W = 0.5; state = 'stable'; }
    else if (spread <= 1) { W = Math.min(1.5, spread + 0.25); state = 'loose'; }
    else { W = 1; state = 'unstable'; }
    return { f: Math.round(Math.pow(2, mean)), spread: spread, n: list.length, autoW: W, state: state };
}

var API = {
    DEPTH_DB: DEPTH_DB, FFT_N: FFT_N, EDGE_BW_OCT: EDGE_BW_OCT, EDGE_MAX_DB: EDGE_MAX_DB, NOISE_RMS: NOISE_RMS,
    bandEdges: bandEdges, notchOrder: notchOrder, designBandstop: designBandstop, designChain: designChain,
    peaking: peaking, shelf: shelf, qFromOct: qFromOct, edgeSections: edgeSections, tiltSections: tiltSections,
    sosResponse: sosResponse, makeState: makeState, sosProcess: sosProcess, makeupDb: makeupDb,
    noiseGen: noiseGen, mulberry32: mulberry32, spectrumProfile: spectrumProfile, profileFromAnalyser: profileFromAnalyser,
    bandShare: bandShare, pinkShare: pinkShare, suitability: suitability, loudnessCompDb: loudnessCompDb,
    matchSummary: matchSummary, _cheb2ap: cheb2ap
};
if (typeof module !== 'undefined' && module.exports) module.exports = API;
if (typeof window !== 'undefined' && window) window.TinnitusDSP = API;
})(typeof globalThis !== 'undefined' ? globalThis : this);
